/**
 * Mappa degli inneschi, disegnata a mano (SPEC-014.2 §B.1).
 *
 * **Non generata a runtime da una libreria di grafi**, di proposito: la
 * topologia cambia una volta ogni sei mesi, e un layout automatico su questi
 * dati produrrebbe un risultato peggiore e più fragile di un disegno fatto una
 * volta bene — oltre a una dipendenza pesante che richiederebbe un ADR.
 *
 * SVG inline e non un file: i colori devono seguire il tema dell'applicazione
 * (chiaro/scuro) tramite le variabili CSS, e un `<img>` non le riceve.
 *
 * ## La legenda non è decorazione
 *
 * La distinzione fra freccia **piena** (conseguenza sempre attiva) e
 * **tratteggiata** (solo con la spunta di conferma) è l'informazione centrale
 * del disegno: è ciò che separa un innesco che può comprare da uno che non può.
 *
 * La distinzione **schedulato vs manuale** è la seconda informazione del
 * disegno dopo la topologia stessa: gli inneschi automatici portano SEMPRE al
 * motore (l'intenzione è il trigger acceso), quelli manuali solo con la spunta
 * (un ordine non deve nascere da un pulsante premuto per leggere un report).
 *
 * ⚠️ Nota per chi confronta col disegno originale in
 * `docs/autotrading/SPEC-014-2-diagram-mappa-inneschi.drawio.xml`: là il nodo
 * del motore riporta «FASE 2 incrocio + 3.2 LLM + 3.1 cancello», con la
 * numerazione **invertita** rispetto all'implementazione. Qui è corretta —
 * 3.1 è la verifica LLM, 3.2 il cancello — come confermato in SPEC-014.
 */

import './automations.css';

type BoxProps = {
  /**
   * Chiave stabile del blocco. Non e' un dettaglio di implementazione: e' il
   * vocabolario con cui il backend dice **quali blocchi una catena ha davvero
   * attraversato** (F-105, `activeBlocks`). Cambiarne uno qui senza cambiarlo
   * la' spegne un'evidenziazione in silenzio.
   */
  id: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  title: string;
  lines?: string[];
  tone: 'manual' | 'auto' | 'proc' | 'safe' | 'danger' | 'test';
  /** Il blocco fa parte della catena che si sta guardando. */
  active?: boolean;
};

function Box({ id, x, y, w = 200, h = 58, title, lines = [], tone, active }: BoxProps) {
  return (
    <g
      className={`flow__box flow__box--${tone} ${active ? 'flow__box--active' : ''}`}
      data-block={id}
    >
      <rect x={x} y={y} width={w} height={h} rx={6} />
      <text x={x + 10} y={y + 20} className="flow__title">
        {title}
      </text>
      {lines.map((line, index) => (
        <text key={line} x={x + 10} y={y + 36 + index * 13} className="flow__sub">
          {line}
        </text>
      ))}
    </g>
  );
}

function Lane({ x, y, w, h, label }: { x: number; y: number; w: number; h: number; label: string }) {
  return (
    <g className="flow__lane">
      <rect x={x} y={y} width={w} height={h} rx={8} />
      <text x={x + 10} y={y + 18} className="flow__lane-label">
        {label}
      </text>
    </g>
  );
}

export function FlowDiagram({
  /**
   * I blocchi attraversati da una catena concreta (F-105). Assente = la mappa
   * generale, senza evidenziazioni: e' cio' che serve alla pagina Automazioni,
   * dove non si sta guardando un caso ma il sistema.
   */
  active,
}: { active?: string[] } = {}) {
  const on = new Set(active ?? []);
  const lit = (id: string) => (active === undefined ? undefined : on.has(id));
  return (
    <div className="flow">
      <svg viewBox="0 0 1240 860" role="img" aria-labelledby="flow-title flow-desc">
        <title id="flow-title">Mappa degli inneschi di LabInvestment</title>
        <desc id="flow-desc">
          Gli inneschi manuali e automatici confluiscono nell&apos;elaborazione. Gli inneschi
          schedulati portano sempre al motore di auto-trading; quelli manuali ci arrivano unicamente
          con la spunta di conferma, perché un ordine non deve poter nascere da un pulsante premuto
          per leggere un report. Il trigger su valore scatta a ogni quotazione scaricata con un confronto
          programmatico, e solo allora una valutazione LLM decide se proporre una vendita, che passa
          comunque dal motore. La scelta delle posizioni da vendere gira una volta al giorno
          all&apos;ora impostata sul portafoglio, guarda le sole posizioni in profitto e propone di
          venderne il 25, 50 o 100 per cento, oppure nessuna; anche queste proposte passano dal
          motore. Aggiornamento quotazioni, sincronizzazione broker e polling analisi
          non hanno conseguenze operative dirette.
        </desc>

        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="flow__arrowhead" />
          </marker>
        </defs>

        {/* Riquadri piu' piccoli e corridoi larghi, con tre canali verticali
            distinti per le frecce: la revisione chiesta dall'utente dopo che
            il disegno era diventato affollato e le frecce si aggrovigliavano. */}
        <Lane x={8} y={8} w={230} h={240} label="INNESCHI MANUALI" />
        <Lane x={8} y={262} w={230} h={550} label="INNESCHI AUTOMATICI" />
        <Lane x={340} y={8} w={250} h={804} label="ELABORAZIONE" />
        <Lane x={820} y={8} w={400} h={330} label="MOTORE DI AUTO-TRADING" />

        {/* Manuali */}
        <Box x={22} y={40} w={200} h={48} tone="manual" id="request_analysis" active={lit('request_analysis')} title="Richiedi analisi" lines={['watchlist · un titolo']} />
        <Box x={22} y={106} w={200} h={48} tone="manual" id="market_report_now" active={lit('market_report_now')} title="Resoconto ora" lines={['Mercati · premium']} />
        <Box x={22} y={172} w={200} h={48} tone="manual" id="generate_now" active={lit('generate_now')} title="Genera adesso" lines={['pagina Briefing']} />

        {/* Automatici */}
        <Box x={22} y={294} w={200} h={48} tone="auto" id="pre_open_trigger" active={lit('pre_open_trigger')} title="Trigger pre-apertura" lines={['1h prima di ogni apertura']} />
        <Box x={22} y={360} w={200} h={48} tone="auto" id="auto_digest" active={lit('auto_digest')} title="Digest automatico" lines={['alla prima apertura utile']} />
        {/* SPEC-019: scatta A OGNI quotazione scaricata, confronto
            programmatico senza LLM. Il nome segue quello dell'utente. */}
        <Box x={22} y={426} w={200} h={56} tone="auto" id="value_trigger" active={lit('value_trigger')} title="Trigger su valore" lines={['a ogni quotazione scaricata', 'confronto programmatico, no LLM']} />
        <Box x={22} y={500} w={200} h={48} tone="auto" id="quotes_sync_polling" active={lit('quotes_sync_polling')} title="Quotazioni · sync · polling" lines={['orario · a budget · 20s']} />
        <Box x={22} y={566} w={200} h={56} tone="auto" id="news" active={lit('news')} title="Raccolta notizie" lines={['ogni 6 ore', 'NON innesca nulla']} />
        <Box x={22} y={640} w={200} h={70} tone="auto" id="close_trigger" active={lit('close_trigger')} title="Trigger di fine giornata" lines={['pre-chiusura e after-hours', 'standard: aggiorna il digest', 'agentico: propone da sé (F-144)']} />
        {/* SPEC-021: l'unico innesco automatico che nasce per VENDERE. Sta qui
            e non fra i manuali perche' parte da sola a un'ora impostata, e la
            freccia verso il motore e' piena: quando propone, consegna sempre. */}
        <Box x={22} y={714} w={200} h={56} tone="auto" id="profit_review" active={lit('profit_review')} title="Scelta posizioni da vendere" lines={["una volta al giorno, all'ora scelta", 'solo posizioni IN PROFITTO']} />

        {/* Elaborazione */}
        <Box x={358} y={40} w={214} h={48} tone="proc" id="tradingagents" active={lit('tradingagents')} title="Analisi TradingAgents" lines={['~19 minuti per titolo']} />
        <Box x={358} y={130} w={214} h={48} tone="proc" id="market_report" active={lit('market_report')} title="Resoconto di mercato" lines={['prosa · notifica']} />
        <Box x={358} y={252} w={214} h={48} tone="proc" id="portfolio_digest" active={lit('portfolio_digest')} title="Digest di portafoglio" lines={['output strutturato']} />
        {/* SPEC-019 §3: LA chiamata LLM del trigger su valore — una sola,
            dopo lo scatto, mai nel percorso caldo delle quotazioni. */}
        <Box x={358} y={392} w={214} h={56} tone="proc" id="value_evaluation" active={lit('value_evaluation')} title="Valutazione su valore" lines={['1 chiamata LLM per scatto', 'sell a step 10% o decline']} />
        <Box x={358} y={520} w={214} h={48} tone="safe" id="no_consequence" active={lit('no_consequence')} title="Nessuna conseguenza" lines={['solo dati e notifiche']} />
        {/* Una sola chiamata al giorno, e solo se c'e' almeno una posizione in
            profitto: senza, la routine non paga nemmeno il modello. */}
        <Box x={358} y={690} w={214} h={56} tone="proc" id="profit_review_evaluation" active={lit('profit_review_evaluation')} title="Revisione dei profitti" lines={['1 chiamata LLM al giorno', 'vendite 25/50/100% o nessuna']} />

        {/* Motore */}
        <Box x={846} y={48} w={348} h={84} tone="proc" id="evaluation" active={lit('evaluation')} title="Valutazione auto-trading" lines={['FASE 2 incrocio · 3.1 verifica LLM', '3.2 cancello · limiti operativi', 'mercato chiuso → rinvio alla sua apertura']} />
        <Box x={846} y={200} w={160} h={60} tone="test" id="dry_run" active={lit('dry_run')} title="dry_run" lines={['registrata, non inviata']} />
        <Box x={1034} y={200} w={160} h={60} tone="danger" id="execute" active={lit('execute')} title="execute" lines={['ORDINE REALE', 'paper/demo/live']} />

        {/* Frecce piene: conseguenza sempre attiva. Tre canali verticali
            (x=262, 290, 318) fra inneschi ed elaborazione, uno per famiglia
            di percorsi: nessuna freccia ne attraversa un'altra. */}
        <g className="flow__edge">
          <path d="M 222 64 L 358 64" markerEnd="url(#arrow)" />
          <path d="M 222 318 L 318 318 L 318 76 L 358 76" markerEnd="url(#arrow)" />
          <path d="M 222 306 L 290 306 L 290 264 L 358 264" markerEnd="url(#arrow)" />
          <path d="M 465 88 L 465 130" markerEnd="url(#arrow)" />
          <path d="M 222 384 L 262 384 L 262 288 L 358 288" markerEnd="url(#arrow)" />
          <path d="M 222 454 L 300 454 L 300 420 L 358 420" markerEnd="url(#arrow)" />
          <path d="M 572 420 L 700 420 L 700 110 L 846 110" markerEnd="url(#arrow)" />
          <path d="M 572 276 L 660 276 L 660 76 L 846 76" markerEnd="url(#arrow)" className="flow__edge--strong" />
          <path d="M 222 668 L 330 668 L 330 154 L 358 154" markerEnd="url(#arrow)" />
          <path d="M 222 524 L 300 524 L 300 544 L 358 544" markerEnd="url(#arrow)" />
          <path d="M 926 132 L 926 200" markerEnd="url(#arrow)" />
          <path d="M 1050 132 L 1114 132 L 1114 200" markerEnd="url(#arrow)" />
          {/* revisione dei profitti → la sua valutazione → il motore. Il canale
              verticale e' x=730, distinto dal 700 del trigger su valore. */}
          <path d="M 222 742 L 290 742 L 290 718 L 358 718" markerEnd="url(#arrow)" />
          <path d="M 572 718 L 730 718 L 730 122 L 846 122" markerEnd="url(#arrow)" className="flow__edge--strong" />
        </g>

        {/* Punteggiate: forniscono DATI, non innescano. */}
        <g className="flow__edge flow__edge--data">
          <path d="M 222 594 L 276 594 L 276 296 L 358 296" markerEnd="url(#arrow)" />
          {/* quotazioni → trigger su valore: il battito che lo alimenta. */}
          <path d="M 122 500 L 122 482" markerEnd="url(#arrow)" />
        </g>

        {/* Tratteggiate: SOLO con la spunta di conferma */}
        <g className="flow__edge flow__edge--conditional">
          <path d="M 222 130 L 248 130 L 248 272 L 358 272" markerEnd="url(#arrow)" />
          <path d="M 222 196 L 236 196 L 236 280 L 358 280" markerEnd="url(#arrow)" />
        </g>

        <text x={648} y={266} className="flow__edge-label flow__edge-label--strong">
          SEMPRE, se attivo sul portafoglio
        </text>
        <text x={604} y={444} className="flow__edge-label">
          solo sell · consegnata al motore: cancello e limiti
        </text>
        <text x={244} y={256} className="flow__edge-label">
          manuali: con la spunta
        </text>
        <text x={240} y={588} className="flow__edge-label">
          contesto letto dal digest, non un innesco
        </text>
        <text x={600} y={742} className="flow__edge-label">
          solo sell su posizioni in profitto · cancello e limiti come le altre
        </text>
      </svg>

      <ul className="flow__legend">
        <li>
          <span className="flow__legend-line" /> conseguenza sempre attiva (percorsi schedulati)
        </li>
        <li>
          <span className="flow__legend-line flow__legend-line--dashed" /> solo manuale, con la spunta di conferma
        </li>
        <li>
          <span className="flow__legend-line flow__legend-line--dotted" /> fornisce dati a chi li legge, non innesca
        </li>
        <li>
          <span className="flow__legend-swatch flow__legend-swatch--safe" /> nessuna conseguenza operativa
        </li>
        <li>
          <span className="flow__legend-swatch flow__legend-swatch--danger" /> ordine reale
        </li>
      </ul>
    </div>
  );
}
